import React, { Fragment, FunctionComponent, useCallback, useState, useMemo } from 'react';
import { InlineCode, Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { TEndpoint } from '@rbx/docsite-utilities/dist/browser';
import getEndpointResourcePathOptions, {
  ResourcePath,
} from '@modules/openCloud/utils/getEndpointResourcePathOptions';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import capitalize from '@modules/article/utils/capitalize';
import getRateLimitMarkdown from '@modules/openCloud/page/limits/utils/getRateLimitMarkdown';
import { TOperationExtensions } from '@rbx/docsite-utilities';
import Layout from './Layout';
import Parameters from './Parameters';
import Response from './Response';
import SchemaTable from './SchemaTable';
import ReturnTitle from './ReturnTitle';
import { HttpMethods } from '../../../../utils/oasTypes';
import EndpointTitle from './EndpointTitle';
import useEndpointStyles from './CloudV2Endpoint.styles';
import ResourcePathItem from '../ResourcePathItem';
import ResourcePathSelectList from '../ResourcePathSelectList';

export interface EndpointProps {
  endpoint: TEndpoint;
  resourceRefName: string;
  resourceName: string;
}

const Endpoint: FunctionComponent<React.PropsWithChildren<EndpointProps>> = ({
  endpoint,
  resourceRefName,
  resourceName,
}) => {
  const {
    classes: {
      marginTop24,
      marginTop64,
      marginBottom8,
      marginTop40,
      marginTop48,
      width100,
      scopes: scopesStyle,
      scopesItem: scopesItemStyle,
    },
    cx,
  } = useEndpointStyles();
  const { translate } = useTranslation();

  const getParametersTitle = useCallback(
    (typeName: string) => {
      if (!typeName) return '';
      const lowerCaseTypeName = typeName.toLowerCase();
      if (lowerCaseTypeName === 'path') {
        return translate('Label.PathParameters');
      }
      if (lowerCaseTypeName === 'query') {
        return translate('Label.QueryParameters');
      }
      return `${capitalize(typeName)} ${translate('Label.Parameters')}`;
    },
    [translate],
  );

  const {
    endpoint: resourcePathVals, // e.g. ['/cloud/v2/universes/{universe_id}/...', ...]
    curl: curlList,
    method,
    title,
    visibility,
    parameterIns: parameterInsList,
    description: endpointDescription,
    scopes: scopesList,
    requestBody: requestBodyList,
    responses: responsesList,
    rateLimits,
    operationId,
  } = endpoint;
  const resourcePathOptions = useMemo(
    () => getEndpointResourcePathOptions(endpoint),
    [endpoint],
  ) as ResourcePath[];
  const [selectedResourcePathName, setSelectedResourcePathName] = useState<string>(
    resourcePathOptions[0].name,
  );

  const listIndex = resourcePathVals.findIndex((resourcePathVal) =>
    resourcePathVal.startsWith(selectedResourcePathName),
  );
  if (listIndex === -1) {
    throw Error(
      `Resource path ${selectedResourcePathName} not found in endpoint ${endpoint.operationId}`,
    );
  }

  const endpointName = resourcePathVals[listIndex];
  const curl = curlList[listIndex];
  const parameterIns = parameterInsList[listIndex];
  const scopes = scopesList[listIndex];
  const requestBody = requestBodyList[listIndex];
  const responses = responsesList[listIndex];

  const codeSamples = [
    {
      title: `${method.toUpperCase()} ${endpointName}`,
      content: curl,
      language: 'bash',
    },
  ];
  if (
    responses?.success &&
    responses?.success.isLongRunning === false &&
    responses.success.exampleValue
  ) {
    codeSamples.push({
      title: translate('Label.Response'),
      content: responses.success.exampleValue,
      language: 'json',
    });
  }

  return (
    <Fragment key={`${method} ${endpointName}`}>
      <Layout resourceName={resourceName} className={marginTop64} codeSamples={codeSamples}>
        <EndpointTitle title={title} visibility={visibility} operationId={operationId} />
        <div className={cx(marginTop24, width100)}>
          {resourcePathOptions.length > 1 ? (
            <ResourcePathSelectList
              resourcePaths={resourcePathOptions}
              method={method as HttpMethods}
              selectedResourcePathName={selectedResourcePathName}
              onSelect={(resourcePath: ResourcePath) =>
                setSelectedResourcePathName(resourcePath.name)
              }
            />
          ) : (
            <ResourcePathItem
              method={method as HttpMethods}
              resourcePathName={selectedResourcePathName}
              active
            />
          )}
        </div>
        {endpointDescription && (
          <StandardMarkdownNoMargin className={marginTop24}>
            {endpointDescription}
          </StandardMarkdownNoMargin>
        )}
        {scopes.length > 0 && (
          <Fragment>
            <Typography variant='subtitle1' className={marginTop24}>
              {translate('Label.Scopes')}
            </Typography>
            <div className={scopesStyle}>
              {scopes.map((scope) => [
                [
                  <InlineCode classes={{ root: scopesItemStyle }} key={scope}>
                    {scope}
                  </InlineCode>,
                ],
              ])}
            </div>
          </Fragment>
        )}
        {rateLimits && (
          <Fragment>
            <Typography variant='subtitle1' className={marginTop24}>
              {translate('Label.Limits')}
            </Typography>
            <StandardMarkdownNoMargin>
              {getRateLimitMarkdown(
                translate,
                rateLimits as TOperationExtensions['x-roblox-rate-limits'] & {
                  perApiKeyOwner: {
                    // eslint-disable-next-line camelcase
                    max_in_period?: number;
                  };
                  perOauth2Authorization: {
                    // eslint-disable-next-line camelcase
                    max_in_period?: number;
                  };
                },
              )}
            </StandardMarkdownNoMargin>
          </Fragment>
        )}
        <Typography variant='h4' className={marginTop40}>
          {translate('Label.Request')}
        </Typography>
        {parameterIns.map(({ name: typeName, parameters }, index: number) => (
          <Fragment key={typeName}>
            <Typography variant='h5' className={index === 0 ? marginTop24 : marginTop48}>
              {getParametersTitle(typeName)}
            </Typography>
            <Parameters className={marginTop24} parameters={parameters} />
          </Fragment>
        ))}
        {requestBody && (
          <Fragment>
            <Typography variant='h5' className={marginTop48}>
              {translate('Label.RequestBody')}
            </Typography>
            {resourceRefName === requestBody.refName ? (
              <ReturnTitle refName={resourceRefName} className={marginTop24} />
            ) : (
              <SchemaTable refName={requestBody.refName} className={marginTop24} />
            )}
          </Fragment>
        )}
        {responses && (
          <Response
            responses={responses}
            resourceRefName={resourceRefName}
            className={marginTop40}
          />
        )}
      </Layout>
    </Fragment>
  );
};

export default Endpoint;
